import { useState, useEffect, createContext, useContext } from 'react';

// Context for user data
const MMUserContext = createContext(null);

// Check if we're on a MachineMetrics domain (including local dev on MM domains)
const isMMDomain = () => {
  const hostname = window.location.hostname;
  return hostname.endsWith('.machinemetrics.com') || 
         hostname.endsWith('.machinemetrics.io') ||
         hostname === 'development.machinemetrics.com';
};

// MM Auth disabled for now - enable when OAuth redirect URIs are configured
// const MM_CLIENT_ID = import.meta.env.VITE_MM_CLIENT_ID;
// const USE_MM_AUTH = !!(MM_CLIENT_ID && isMMDomain());
const USE_MM_AUTH = false;

/**
 * Hook to fetch and manage MachineMetrics user data
 * 
 * When deployed to MM platform, uses official mm-react-tools.
 * For local development, uses mock user data.
 */
export function useMMUser() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      // For local development, use mock user
      if (!USE_MM_AUTH) {
        setUser({
          id: 'dev-user-001',
          email: 'developer@machinemetrics.com',
          name: 'Dev User',
          level: 'root',
          accountId: 'dev-account-001',
          accountName: 'Development Account',
        });
        setLoading(false);
        return;
      }

      // On MM domain, try to fetch real user data
      try {
        const { useMMAuth } = await import('@machinemetrics/mm-react-tools');
        // This would need to be called from within the MMProvider context
        // For now, we'll handle this in a separate component
      } catch (err) {
        console.error('Failed to load MM auth:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const isRoot = user?.level === 'root';
  const isAdmin = user?.level === 'admin' || isRoot;
  const canCreateCompany = isRoot;

  return { 
    user, 
    loading, 
    error, 
    isRoot, 
    isAdmin, 
    canCreateCompany,
    isAuthenticated: !!user,
    isLocalDev: !USE_MM_AUTH,
  };
}

/**
 * Provider component for MM user context
 */
export function MMUserProvider({ children }) {
  const userState = useMMUser();
  
  return (
    <MMUserContext.Provider value={userState}>
      {children}
    </MMUserContext.Provider>
  );
}

/**
 * Hook to access MM user from context
 */
export function useMMUserContext() {
  const context = useContext(MMUserContext);
  if (!context) {
    throw new Error('useMMUserContext must be used within MMUserProvider');
  }
  return context;
}

export default useMMUser;
